import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BestellingDorpelDto, DorpelDto } from 'src/app/shared/api-models/models';
import { DorpelDecorator } from 'src/app/shared/models/dorpel-decorator';
import { filter } from 'rxjs/operators';
import { BestellingNeutDto, NeutDto, BestellingBewerkingDto, DealerDto } from '../../shared/api-models/models';
import { BewerkingUtils, VerstekOrientatie } from '../../shared/api-models/bewerkingDto';
import { ModelFactory } from '../../shared/models/model-factory';
import { DEALER_RESOURCES_STORAGE_KEY, DealerCustomResources, DEFAULT_DEALER_RESOURCE_VALUES } from '../../dealers/custom-dealer-resource-map';
import { VorsponningSize } from 'src/app/shared/models/neut.model';
import { ASSETS_BASE_PATH } from 'src/app/shared/constants';

export interface FormState {
  aantalNeuten?: number;
  aantalSluitpotten?: number;
  dorpel?: DorpelDto;
}
interface SponningOptions {
  isActive: boolean;
  isLeftSide: boolean;
  isRightSide: boolean;
  leftCustomDepth: number;
  leftCustomLength: number;
  leftCustomOffset: number;
  rightCustomDepth: number;
  rightCustomLength: number;
  rightCustomOffset: number;
  leftOrientatie?: VerstekOrientatie;
  rightOrientatie?: VerstekOrientatie;
}

@Injectable()
export class UiStateService {

  public viewMode: 'Edit' | 'Add';

  public logoUrl: string;

  public fromPrefilledOrder: boolean = false;

  private _currentDorpel: BestellingDorpelDto;

  private _isDrawingInExampleMode: boolean;

  private _currentProductType: string;

  private _railType: string | null;

  private _currentDealersGroup: DealerDto[] | null;

  private _customDealerResources: DealerCustomResources | null;

  private _redrawCount = 0;

  public dorpelChoices: DorpelDto[] = [];

  public neutenChoices: NeutDto[] = [];

  public kloosterSponningOptions: SponningOptions;

  public renovatieSponningOptions: SponningOptions;

  public verstekSponningOptions: SponningOptions;

  public setNeutenChoices(choices: NeutDto[]): void {
    this.neutenChoices = choices;
  }

  public turnOnDrawingExample() {
    this._isDrawingInExampleMode = true;
  }

  public turnOffDrawingExample() {
    this._isDrawingInExampleMode = false;
  }

  public isDrawingInExampleMode(): boolean {
    return this._isDrawingInExampleMode;
  }

  public getNeutDtoByCodeAndPositie(neutCode: string, positie: string): NeutDto {
    return this.neutenChoices.find(neut => neut.neutCode === neutCode && neut.positie === positie);
  }

  public get currentProductType(): string {
    return this._currentProductType;
  }

  public get currentDorpel(): BestellingDorpelDto {
    return this._currentDorpel;
  }

  public get currentDealersGroup(): DealerDto[] | null {
    return this._currentDealersGroup;
  }

  public get customDealerResources(): DealerCustomResources | null {
    return this._customDealerResources;
  }
  public get railType(): string | null {
    return this._railType;
  }

  public get isCurrentDorpelSliding(): boolean {
    const dorpelCode = this.currentDorpel.dorpelCode;
    const chosenDorpel = this.dorpelChoices.find(d => d.dorpelCode === dorpelCode);
    let isCurrentDoorSliding = chosenDorpel.dorpelsoort === "Hefschuifdorpel";

    return isCurrentDoorSliding;
  }

  private _sluitpottenCount$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get sluitpottenCount$(): Observable<number> {
    //we need to notify empty field values or 0s to disable fields as part of the field dependency work
    return this._sluitpottenCount$.asObservable()/*.pipe(filter(count => !!count))*/;
  }

  private _customDealerResources$: BehaviorSubject<DealerCustomResources> = new BehaviorSubject<DealerCustomResources>(null);

  public get customDealerResources$(): Observable<DealerCustomResources> {
    return this._customDealerResources$.asObservable()
  }

  private _redraw$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get redraw$(): Observable<number> {
    return this._redraw$.asObservable();
  }

  public triggerRedraw(): void {
    this._redrawCount += 1;
    this._redraw$.next(this._redrawCount);
  }

  private _neutenCount$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get neutenCount$(): Observable<number> {
    //we need to notify empty field values or 0s to disable fields as part of the field dependency work
    return this._neutenCount$.asObservable()/*.pipe(filter(count => !!count))*/;
  }

  private _schema$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public get schema$(): Observable<string> {
    return this._schema$.asObservable().pipe(filter(schema => !!schema));
  }

  private _productType$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public get productType$(): Observable<string> {
    return this._productType$.asObservable().pipe(filter(p => !!p));
  }

  //CUSTOM WIDTH FOR SPECIAL NEUTEN
  private _neutCustomWidthUpdateSubject = new BehaviorSubject<any>(null);
  public neutCustomWidthUpdate$ = this._neutCustomWidthUpdateSubject.asObservable();
  public updateSpecialNeutCustomWidth(width: number): void {
    this._neutCustomWidthUpdateSubject.next(width);
  }

  public setProductType(productType: string): void {
    this._currentProductType = productType
    this._productType$.next(productType);
  }

  private _dorpelCode$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public get dorpelCode$(): Observable<string> {
    return this._dorpelCode$.asObservable().pipe(filter(code => !!code));
  }

  private _zaagmaat$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get zaagmaat$(): Observable<number> {
    return this._zaagmaat$.asObservable().pipe(filter(value => !!value));
  }

  private _maatA$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get maatA$(): Observable<number> {
    return this._maatA$.asObservable().pipe(filter(value => !!value));
  }

  private _maatZ$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get maatZ$(): Observable<number> {
    return this._maatZ$.asObservable().pipe(filter(value => !!value));
  }

  private _deurmaatA$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get deurmaatA$(): Observable<number> {
    return this._deurmaatA$.asObservable().pipe(filter(value => !!value));
  }

  private _deurmaatB$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get deurmaatB$(): Observable<number> {
    return this._deurmaatB$.asObservable().pipe(filter(value => !!value));
  }

  private _maatB$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get maatB$(): Observable<number> {
    return this._maatB$.asObservable().pipe(filter(value => !!value));
  }

  private _firstMaatA$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get firstMaatA$(): Observable<number> {
    return this._firstMaatA$.asObservable().pipe(filter(value => !!value));
  }

  private _deurmaatC$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get deurmaatC$(): Observable<number> {
    return this._deurmaatC$.asObservable().pipe(filter(value => !!value));
  }

  private _maatC$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public get maatC$(): Observable<number> {
    return this._maatC$.asObservable().pipe(filter(value => !!value));
  }

  private _neuten$: BehaviorSubject<BestellingNeutDto[]> = new BehaviorSubject<BestellingNeutDto[]>([]);

  public get neuten$(): Observable<BestellingNeutDto[]> {
    return this._neuten$.asObservable().pipe(filter(value => !!value));
  }

  public setCurrentDorpel(dorpelOrder: BestellingDorpelDto, preventDataReset: boolean = true): void {
    if (!dorpelOrder) {
      throw new Error('dorpelOrder should not be null!');
    }
    this._currentDorpel = dorpelOrder;
    this.setSluitpottenCount(this.currentDorpel.aantalSluitpotten);
    this.setDorpelCode(this.currentDorpel.dorpelCode, preventDataReset); //call with prevent dorpel data reset
    this.setNeutenCount(this.currentDorpel.aantalNeuten);
    this.setSchema(this.currentDorpel.schema);
    this.setZaagmaat(this.currentDorpel.zaagmaat);
    this.setMaatA(this.currentDorpel.maatA);
    this.setFirstMaatA(this.currentDorpel.firstMaatA);
    this.setDeurmaatA(this.currentDorpel.deurmaatA);
    this.setMaatB(this.currentDorpel.maatB);
    this.setDeurmaatB(this.currentDorpel.deurmaatB);
    this.setMaatC(this.currentDorpel.maatC);
    this.setDeurmaatC(this.currentDorpel.deurmaatC);
    this.setMaatZ(this.currentDorpel.maatZ);
  }

  public setNeutenCount(count: number): void {
    DorpelDecorator.setNumberOfNeuten(this.currentDorpel, count);
    this._neutenCount$.next(count);
  }

  private getResetSponningOptions(): SponningOptions {
    return {
      isActive: false,
      isLeftSide: false,
      isRightSide: false,
      leftCustomDepth: null,
      leftCustomLength: null,
      leftCustomOffset: null,
      rightCustomDepth: null,
      rightCustomLength: null,
      rightCustomOffset: null,
      leftOrientatie: "naarBuiten",
      rightOrientatie: "naarBuiten"
    }
  }

  public setBewerkingenOptions(selectedBwCodes: BestellingBewerkingDto[]): void {
    let leftKloostersponningActive = false;
    let leftRenovatiesponningActive = false;
    let rightKloostersponningActive = false;
    let rightRenovatiesponningActive = false;
    let leftVersteksponningActive = false;
    let rightVersteksponningActive = false;

    let leftKloostersponningCustomDepth = null;
    let leftRenovatiesponningCustomDepth = null;
    let rightKloostersponningCustomDepth = null;
    let rightRenovatiesponningCustomDepth = null;
    let leftVersteksponningCustomDepth = null;
    let rightVersteksponningCustomDepth = null;

    let leftKloostersponningCustomOffset = null;
    let leftRenovatiesponningCustomOffset = null;
    let rightKloostersponningCustomOffset = null;
    let rightRenovatiesponningCustomOffset = null;
    let leftVersteksponningCustomOffset = null;
    let rightVersteksponningCustomOffset = null;

    let leftKloostersponningCustomLength = null;
    let leftRenovatiesponningCustomLength = null;
    let rightKloostersponningCustomLength = null;
    let rightRenovatiesponningCustomLength = null;
    let leftVersteksponningCustomLength = null;
    let rightVersteksponningCustomLength = null;

    let leftVerstekSponningOrientatie = "naarBuiten" as VerstekOrientatie;
    let rightVerstekSponningOrientatie = "naarBuiten" as VerstekOrientatie;

    if (!selectedBwCodes.length) {
      this.kloosterSponningOptions = this.getResetSponningOptions();
      this.renovatieSponningOptions = this.getResetSponningOptions();
      this.verstekSponningOptions = this.getResetSponningOptions();
    } else {
      selectedBwCodes.forEach((bw, index) => {
        if (BewerkingUtils.isKloostersponningLeft(bw.description)) {
          leftKloostersponningActive = true;
          leftKloostersponningCustomDepth = bw.customDepth;
          leftKloostersponningCustomLength = bw.customLength;
          leftKloostersponningCustomOffset = bw.customOffset;
        }

        if (BewerkingUtils.isKloostersponningRight(bw.description)) {
          rightKloostersponningActive = true;
          rightKloostersponningCustomDepth = bw.customDepth;
          rightKloostersponningCustomLength = bw.customLength;
          rightKloostersponningCustomOffset = bw.customOffset;
        }

        if (BewerkingUtils.isKloostersponningLeftAndRight(bw.description)) {
          leftKloostersponningActive = true;
          rightKloostersponningActive = true;
          leftKloostersponningCustomDepth = bw.customDepth;
          leftKloostersponningCustomLength = bw.customLength;
          leftKloostersponningCustomOffset = bw.customOffset;
          rightKloostersponningCustomDepth = bw.customDepth;
          rightKloostersponningCustomLength = bw.customLength;
          rightKloostersponningCustomOffset = bw.customOffset;
        }

        if (BewerkingUtils.isRenovatiesponningLeft(bw.description)) {
          leftRenovatiesponningActive = true;
          leftRenovatiesponningCustomDepth = bw.customDepth;
          leftRenovatiesponningCustomLength = bw.customLength;
          leftRenovatiesponningCustomOffset = bw.customOffset;
        }

        if (BewerkingUtils.isRenovatiesponningRight(bw.description)) {
          rightRenovatiesponningActive = true;
          rightRenovatiesponningCustomDepth = bw.customDepth;
          rightRenovatiesponningCustomLength = bw.customLength;
          rightRenovatiesponningCustomOffset = bw.customOffset;
        }

        if (BewerkingUtils.isRenovatiesponningLeftAndRight(bw.description)) {
          leftRenovatiesponningActive = true;
          rightRenovatiesponningActive = true;
          leftRenovatiesponningCustomDepth = bw.customDepth;
          leftRenovatiesponningCustomLength = bw.customLength;
          leftRenovatiesponningCustomOffset = bw.customOffset;
          rightRenovatiesponningCustomDepth = bw.customDepth;
          rightRenovatiesponningCustomLength = bw.customLength;
          rightRenovatiesponningCustomOffset = bw.customOffset;
        }

        if (BewerkingUtils.isVersteksponningLeft(bw.description)) {
          leftVersteksponningActive = true;
          leftVerstekSponningOrientatie = bw.orientatie;
        }

        if (BewerkingUtils.isVersteksponningRight(bw.description)) {
          rightVersteksponningActive = true;
          rightVerstekSponningOrientatie = bw.orientatie;
        }
      });

      this.renovatieSponningOptions = {
        isLeftSide: leftRenovatiesponningActive,
        isRightSide: rightRenovatiesponningActive,
        isActive: leftRenovatiesponningActive || rightRenovatiesponningActive,
        leftCustomDepth: leftRenovatiesponningCustomDepth,
        leftCustomLength: leftRenovatiesponningCustomLength,
        leftCustomOffset: leftRenovatiesponningCustomOffset,
        rightCustomDepth: rightRenovatiesponningCustomDepth,
        rightCustomLength: rightRenovatiesponningCustomLength,
        rightCustomOffset: rightRenovatiesponningCustomOffset
      }

      this.kloosterSponningOptions = {
        isLeftSide: leftKloostersponningActive,
        isRightSide: rightKloostersponningActive,
        isActive: leftKloostersponningActive || rightKloostersponningActive,
        leftCustomDepth: leftKloostersponningCustomDepth,
        leftCustomLength: leftKloostersponningCustomLength,
        leftCustomOffset: leftKloostersponningCustomOffset,
        rightCustomDepth: rightKloostersponningCustomDepth,
        rightCustomLength: rightKloostersponningCustomLength,
        rightCustomOffset: rightKloostersponningCustomOffset
      }

      //verstek custom depths are always null
      this.verstekSponningOptions = {
        isLeftSide: leftVersteksponningActive,
        isRightSide: rightVersteksponningActive,
        isActive: leftVersteksponningActive || rightVersteksponningActive,
        leftCustomDepth: leftVersteksponningCustomDepth,
        leftCustomLength: leftVersteksponningCustomLength,
        leftCustomOffset: leftVersteksponningCustomOffset,
        rightCustomDepth: rightVersteksponningCustomDepth,
        rightCustomLength: rightVersteksponningCustomLength,
        rightCustomOffset: rightVersteksponningCustomOffset,
        leftOrientatie: leftVerstekSponningOrientatie,
        rightOrientatie: rightVerstekSponningOrientatie
      }
    }
  }

  public setDorpelCode(dorpelCode: string, preventDataReset?: boolean): void {
    this.currentDorpel.dorpelCode = dorpelCode;
    this.kloosterSponningOptions = this.getResetSponningOptions();
    this.renovatieSponningOptions = this.getResetSponningOptions();
    this.verstekSponningOptions = this.getResetSponningOptions();

    if (!preventDataReset && !this.fromPrefilledOrder) {
      //reset neuten data
      for (let i = 0; i < this.currentDorpel.neuten.length; i++) {
        this.currentDorpel.neuten[i] = ModelFactory.createNeut();
      }

      //reset sluitpotten data
      for (let i = 0; i < this.currentDorpel.sluitpotten.length; i++) {
        this.currentDorpel.sluitpotten[i] = ModelFactory.createSluitpot();
      }

      //RESET VALUES
      this.currentDorpel.bewerkingen = [];
      this.currentDorpel.firstMaatA = 0;
      this.currentDorpel.deurmaatA = 0;
      this.currentDorpel.deurmaatB = 0;
      this.currentDorpel.deurmaatC = 0;
      this.currentDorpel.maatA = 0;
      this.currentDorpel.maatB = 0;
      this.currentDorpel.maatC = 0;
      this.currentDorpel.maatZ = 0;
      this.currentDorpel.zaagmaat = 0;
    }

    this._dorpelCode$.next(dorpelCode);
    this._neuten$.next(this.currentDorpel.neuten);
  }

  public setRailType(type: string | null) {
    this.currentDorpel.railType = type;
    this._railType = type;
  }

  public setZaagmaat(zaagmaat: number): void {
    this.currentDorpel.zaagmaat = zaagmaat;
    this._zaagmaat$.next(zaagmaat);
  }

  public setMaatA(maatA: number): void {
    this.currentDorpel.maatA = maatA;
    this._maatA$.next(maatA);
  }

  public setMaatZ(maatZ: number): void {
    this.currentDorpel.maatZ = maatZ;
    this._maatZ$.next(maatZ);
  }

  public setDeurmaatA(deurmaatA: number): void {
    this.currentDorpel.deurmaatA = deurmaatA;
    this._deurmaatA$.next(deurmaatA);
  }

  public setDeurmaatB(deurmaatB: number): void {
    this.currentDorpel.deurmaatB = deurmaatB;
    this._deurmaatB$.next(deurmaatB);
  }

  public setMaatB(maatB: number): void {
    this.currentDorpel.maatB = maatB;
    this._maatB$.next(maatB);
  }

  public setFirstMaatA(firstMaatA: number): void {
    this.currentDorpel.firstMaatA = firstMaatA;
    this._firstMaatA$.next(firstMaatA);
  }

  public setDeurmaatC(deurmaatC: number): void {
    this.currentDorpel.deurmaatC = deurmaatC;
    this._deurmaatC$.next(deurmaatC);
  }

  public setMaatC(maatC: number): void {
    this.currentDorpel.maatC = maatC;
    this._maatC$.next(maatC);
  }

  public updateDaagmaat(daagmaat: number, index: number): void {
    this.currentDorpel.neuten[index].daagmaat = daagmaat;
  }

  public updateGlaslat(glaslat: string, index: number): void {
    this.currentDorpel.neuten[index].glaslat = glaslat;
  }

  public updateBeglazingsprofiel(beglazingsprofiel: string, index: number): void {
    this.currentDorpel.neuten[index].beglazingsprofiel = beglazingsprofiel;
  }

  public updateAanslagprofiel(aanslagprofiel: string, index: number): void {
    this.currentDorpel.neuten[index].aanslagprofiel = aanslagprofiel;
  }

  public updateDamverbreeding(damverbreeding: number, index: number): void {
    this.currentDorpel.neuten[index].damverbreeding = damverbreeding;
  }

  public updateSpooningsoort(sponningsoort: string, index: number): void {
    this.currentDorpel.neuten[index].sponningsoort = sponningsoort;
  }

  public updateSponningmaat(sponningmaat: number, index: number): void {
    this.currentDorpel.neuten[index].sponningmaat = sponningmaat;
  }

  public setSluitpottenCount(count: number): void {
    DorpelDecorator.setNumberOfSluitpotten(this.currentDorpel, count);
    this._sluitpottenCount$.next(count);
  }

  public setSchema(schema: string): void {
    this.currentDorpel.schema = schema;
    this._schema$.next(schema);
  }

  public populateNeut(neutData: Partial<BestellingNeutDto>, index: number): void {
    let target = this.currentDorpel.neuten[index];
    let keys = Object.keys(neutData);
    keys.forEach(key => {
      target[key] = neutData[key];
    });
    this._neuten$.next(this.currentDorpel.neuten);
  }

  public populateLeftVoorsponning(neutIndex: number, voorsponningSize: VorsponningSize) {
    this.currentDorpel.neuten[neutIndex].linksVoorsponning = voorsponningSize;
  }

  public populateRightVoorsponning(neutIndex: number, voorsponningSize: VorsponningSize) {
    this.currentDorpel.neuten[neutIndex].rechtsVoorsponning = voorsponningSize;
  }

  public updateSluitpot(sluitpodData, index): void {
    this.currentDorpel.sluitpotten[index] = sluitpodData;
  }

  public setLogoUrl(url: string) {
    this.logoUrl = url;
  }

  public setCurrentDealersGroup(dealers: DealerDto[] | null) {
    this._currentDealersGroup = dealers;
  }

  public setCustomDealerResources(dealer?: Partial<DealerDto>) {
    if (dealer) {
      this._customDealerResources = {
        primaryColor: dealer.primary,
        secondaryColor: dealer.secondary,
        textColor: dealer.text,
        logoLink: dealer.image,
        dealerGUID: dealer.sleutel
      };
      sessionStorage.setItem(DEALER_RESOURCES_STORAGE_KEY, JSON.stringify(this._customDealerResources))
      document.documentElement.style.setProperty('--primary-color', dealer.primary);
      document.documentElement.style.setProperty('--secondary-color', dealer.secondary);
      document.documentElement.style.setProperty('--text-color', dealer.text);

    } else {
      this._customDealerResources = null;
      sessionStorage.removeItem(DEALER_RESOURCES_STORAGE_KEY)
      document.documentElement.style.setProperty('--primary-color', DEFAULT_DEALER_RESOURCE_VALUES.primaryColor);
      document.documentElement.style.setProperty('--secondary-color', DEFAULT_DEALER_RESOURCE_VALUES.secondaryColor);
      document.documentElement.style.setProperty('--text-color', DEFAULT_DEALER_RESOURCE_VALUES.textColor);
    }

    this._customDealerResources$.next(this._customDealerResources);
  }
}
